import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DateRangePicker, {
  DateRangePickerDateSelectFunction,
} from '~components/Filters/DateRangePicker';
import { SearchParams } from '~constants/enums';
import {
  persistDateRange,
  retrievePersistedDateRange,
} from '~utils/dateRangePersistence';

import DriverPayQueryContext from '../../DriverPayQueryContext';
import { driverPayDateRangeStorageKey } from '../../utils';

const deriveDateRangeFromDriverPayQueryFilters = (
  startDate: Dayjs | undefined,
  endDate: Dayjs | undefined,
) => {
  const start = startDate ? dayjs.tz(startDate) : null;
  const end = endDate ? dayjs.tz(endDate) : null;

  return [start, end];
};

export default function DateRangeFilter() {
  const driverPayQuery = useContext(DriverPayQueryContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialDateRate = useMemo(() => {
    return deriveDateRangeFromDriverPayQueryFilters(
      driverPayQuery.filters.startDate,
      driverPayQuery.filters.endDate,
    );
  }, [driverPayQuery.filters.startDate, driverPayQuery.filters.endDate]);

  const [selecteDateRange, setSelectedDateRange] = useState([...initialDateRate]);

  const handleDateSelect = useCallback<DateRangePickerDateSelectFunction>(
    (startDate, endDate) => {
      setSelectedDateRange([startDate, endDate ?? startDate]);
    },
    [],
  );

  const handleAnchorClose = useCallback(() => {
    const startDate = selecteDateRange[0] ?? undefined;
    const endDate = selecteDateRange[1] ?? undefined;

    const startDateDidChange =
      driverPayQuery.filters.startDate?.toString() !== startDate?.toString();
    const endDateDidChange =
      driverPayQuery.filters.endDate?.toString() !== endDate?.toString();

    if (startDateDidChange || endDateDidChange) {
      // Update the context
      driverPayQuery.applyFilters({ startDate, endDate });

      // A cleared range persists as explicit nulls: the user chose "All dates"
      // and must not be re-hydrated into the previous range.
      persistDateRange(
        driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
        {
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        },
      );

      // Update the URL parameters
      setSearchParams((params) => {
        if (startDate) {
          params.set(SearchParams.START_DATE, startDate.startOf('day').toISOString());
        } else {
          params.delete(SearchParams.START_DATE);
        }
        if (endDate) {
          params.set(SearchParams.END_DATE, endDate.endOf('day').toISOString());
        } else {
          params.delete(SearchParams.END_DATE);
        }
        return params;
      });
    }
  }, [
    selecteDateRange[0],
    selecteDateRange[1],
    driverPayQuery.filters.startDate,
    driverPayQuery.filters.endDate,
    searchParams,
  ]);

  // Sync URL parameters to context when they change externally
  useEffect(() => {
    // Normalize the "absent" value on both sides: searchParams.get() returns null
    // while the context stores undefined, and `null !== undefined` would fire a
    // redundant applyFilters (and thus a duplicate refetch) after every URL
    // cleanup, e.g. clearAllFiltersExceptSearch.
    const urlStartDate = searchParams.get(SearchParams.START_DATE) ?? undefined;
    const urlEndDate = searchParams.get(SearchParams.END_DATE) ?? undefined;

    // A fully paramless URL usually means internal navigation dropped the date
    // params (the Payables/Receivables nav links carry none).
    // Re-inject this category's persisted range before the compare below would
    // treat "absent" as "All dates". Stored explicit nulls (the user cleared)
    // or an empty key inject nothing; partial deep links are left untouched.
    if (!urlStartDate && !urlEndDate) {
      const storedDateRange = retrievePersistedDateRange(
        driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
      );
      const restoredStartDate = storedDateRange?.startDate
        ? dayjs.tz(storedDateRange.startDate)
        : undefined;
      const restoredEndDate = storedDateRange?.endDate
        ? dayjs.tz(storedDateRange.endDate)
        : undefined;

      if (restoredStartDate || restoredEndDate) {
        // Apply the context value from the clean stored dates rather than
        // waiting for the URL re-parse: `dayjs.tz(isoWithZ)` reinterprets the
        // UTC wall clock in the default zone, which would shift the end date
        // by a day. The URL gets the picker's own encoding, so this effect's
        // next run compares equal and does not re-apply.
        driverPayQuery.applyFilters({
          startDate: restoredStartDate,
          endDate: restoredEndDate,
        });

        setSearchParams(
          (params) => {
            if (restoredStartDate) {
              params.set(
                SearchParams.START_DATE,
                restoredStartDate.startOf('day').toISOString(),
              );
            }
            if (restoredEndDate) {
              params.set(
                SearchParams.END_DATE,
                restoredEndDate.endOf('day').toISOString(),
              );
            }
            return params;
          },
          { replace: true },
        );

        return;
      }
    }

    const contextStartDate = driverPayQuery.filters.startDate
      ?.startOf('day')
      .toISOString();
    const contextEndDate = driverPayQuery.filters.endDate?.endOf('day').toISOString();

    if (urlStartDate !== contextStartDate || urlEndDate !== contextEndDate) {
      driverPayQuery.applyFilters({
        startDate: urlStartDate ? dayjs(urlStartDate).tz() : undefined,
        endDate: urlEndDate ? dayjs(urlEndDate).tz() : undefined,
      });
    }
    // We don't need to depend on driverPayQuery.filters.startDate and driverPayQuery.filters.endDate
    // because we want to sync on URL parameters changes
  }, [searchParams]);

  useEffect(() => {
    const dateRange = deriveDateRangeFromDriverPayQueryFilters(
      driverPayQuery.filters.startDate,
      driverPayQuery.filters.endDate,
    );

    setSelectedDateRange(dateRange);
  }, [driverPayQuery.filters.startDate, driverPayQuery.filters.endDate]);

  return (
    <DateRangePicker
      allowAllDates
      endDate={selecteDateRange[1]}
      mode="icon"
      onAnchorClose={handleAnchorClose}
      onDateSelect={handleDateSelect}
      showShortcuts
      startDate={selecteDateRange[0]}
      useTodayFormat
    />
  );
}
